/**
 * Empty state for the customers list — first-run + filter-no-match.
 */
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { Card, CardBody } from "@flint/ui";

export interface EmptyStateProps {
  /** "all" first-run vs "archived" vs filtered-search vs UI tab (overdue/active/new). */
  variant: "first-run" | "no-archived" | "no-search-match" | "no-tab-match";
  query?: string;
  tab?: "overdue" | "active" | "new";
}

const TAB_EMPTY_COPY: Record<
  NonNullable<EmptyStateProps["tab"]>,
  { title: string; hint: string }
> = {
  overdue: {
    title: "Nothing overdue right now.",
    hint: "Every customer with an issued invoice is within its due date.",
  },
  active: {
    title: "No customers active in the last 30 days.",
    hint: "Recent-activity customers appear here after their next invoice is issued.",
  },
  new: {
    title: "No new customers in the last 30 days.",
    hint: "Customers added in the past month show up here.",
  },
};

export function EmptyState({ variant, query, tab }: EmptyStateProps) {
  if (variant === "no-search-match") {
    return (
      <Card>
        <CardBody className="text-center py-10">
          <p className="font-medium">
            No customers match {query ? <>“{query}”</> : "this search"}.
          </p>
          <p className="text-sm text-muted mt-1">Try a name prefix, or paste the full GSTIN.</p>
        </CardBody>
      </Card>
    );
  }

  if (variant === "no-tab-match" && tab) {
    const copy = TAB_EMPTY_COPY[tab];
    return (
      <Card>
        <CardBody className="text-center py-10">
          <p className="font-medium">{copy.title}</p>
          <p className="text-sm text-muted mt-1">{copy.hint}</p>
        </CardBody>
      </Card>
    );
  }

  if (variant === "no-archived") {
    return (
      <Card>
        <CardBody className="text-center py-10">
          <p className="font-medium">No archived customers.</p>
          <p className="text-sm text-muted mt-1">Archived rows show up here once you retire one.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className="text-center py-10">
        <p className="text-base font-medium">No customers yet.</p>
        <p className="text-sm text-muted mt-1 max-w-md mx-auto">
          Add your first buyer to start raising invoices. You can paste a GSTIN to fill the tax
          details, or skip and add them later.
        </p>
        <Link
          to="/sales/customers/new"
          className="inline-flex items-center justify-center gap-1.5 mt-4 h-9 px-4 rounded-sm bg-ink text-paper text-sm font-medium hover:bg-ink-2"
        >
          <Plus size={13} strokeWidth={2.4} aria-hidden />
          Add your first customer
        </Link>
      </CardBody>
    </Card>
  );
}
